import { AddCircle, Cancel, CheckCircle, Launch } from "@mui/icons-material"
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridToolbar,
} from "@mui/x-data-grid"
import { StyledButton } from "components/Button/StyledButton"
import { HaufeLoader, HaufeLoaderInline } from "components/HaufeLoader/HaufeLoader"
import { useHaufeLoading } from "contexts/HaufeLoadingContext"
import { Lead } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { AppRoutes } from "store/routes"

const Leads = () => {
  const navigate = useNavigate()
  const { leads, leadsArchive, customers, projects } = useStoreState((state) => state)
  const { fetchLeads, fetchCustomers, fetchLeadsArchive, fetchSalesQuotes, fetchProjects } =
    useStoreActions((actions) => actions)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [loadingArchive, setLoadingArchive] = useState(false)

  const { isHaufeLoading, haufeOperationType } = useHaufeLoading()

  // Helper function to check if a project exists for this opportunity
  const hasExistingProject = (opportunityID: string) => {
    // Safety checks to prevent errors when projects array is empty, undefined, or malformed
    if (!projects || !Array.isArray(projects) || projects.length === 0) {
      return false
    }

    if (!opportunityID) {
      return false
    }

    return projects.some((project) => {
      // Additional safety checks for project structure
      return project?.lead?.opportunityID === opportunityID
    })
  }

  useEffect(() => {
    const loadLeads = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch core data first (leads, customers, sales quotes)
        await Promise.all([
          fetchLeads({ fresh: false }),
          fetchCustomers({ fresh: false }),
          fetchSalesQuotes({ fresh: false }),
        ])

        // Fetch projects separately with its own error handling
        try {
          await fetchProjects({ fresh: false })
        } catch (projectsError) {
          console.warn("Failed to load projects (icons will default to 'create'):", projectsError)
          // Don't set main error - the page can still function without projects data
          // Projects array will remain empty, so all icons will show "create project"
        }
      } catch (err) {
        console.error("Error loading core data:", err)
        setError("Fehler beim Laden der Verkaufschancen. Bitte versuchen Sie es später erneut.")
      } finally {
        setLoading(false)
      }
    }

    loadLeads()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoadArchived = async () => {
    if (loadingArchive) return

    try {
      setLoadingArchive(true)
      await fetchLeadsArchive({ fresh: false })
      setShowArchived(true)
    } catch (err) {
      console.error("Error loading archived leads:", err)
      setError("Failed to load archived opportunities.")
    } finally {
      setLoadingArchive(false)
    }
  }

  const handleLoadLeads = async () => {
    if (loading) return
    try {
      setLoading(true)
      setError(null)
      await fetchLeads({ fresh: true })
    } catch (err) {
      console.error("Error loading leads:", err)
      setError("Failed to load leads.")
    } finally {
      setLoading(false)
    }
  }

  const businessAccountToCustomer = (businessAccount: string) => {
    const customer = customers.find((c) => c.customerID === businessAccount)
    return customer?.customerName || businessAccount
  }

  const columns: GridColDef<Lead>[] = [
    {
      field: "opportunityID",
      headerName: "ID",
      flex: 0.5,
      sortable: true,
    },
    {
      field: "subject",
      headerName: "Titel/Name",
      flex: 1,
      sortable: true,
    },
    {
      field: "businessAccount",
      headerName: "Kunde",
      flex: 1,
      sortable: true,

      valueGetter: (params: string) => {
        return businessAccountToCustomer(params || "")
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      sortable: true,
    },
    {
      field: "source",
      headerName: "Aktiv",
      flex: 0.2,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Lead>) => {
        const isArchived = leadsArchive.some((l) => l.id === params.row.id)
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            {isArchived ? (
              <Cancel sx={{ color: "error.main" }} />
            ) : (
              <CheckCircle sx={{ color: "success.main" }} />
            )}
          </Box>
        )
      },
    },
    {
      field: "estimatedCloseDate",
      headerName: "Geplantes Abschlussdatum",
      flex: 1,
      sortable: true,

      valueGetter: (params: string) => {
        if (!params) return "-"
        const date = new Date(params)
        return date.toLocaleDateString("de-DE")
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Projekt",
      sortable: false,
      getActions: (params: GridRowParams) => {
        const projectExists = hasExistingProject(params.row.opportunityID)
        return [
          <Tooltip
            title={projectExists ? "Projekt öffnen" : "Projekt erstellen"}
            key={`project-action-${params.row.opportunityID}`}
          >
            <span>
              <IconButton
                onClick={() => navigate(`${AppRoutes.PROJECTS}/${params.row.opportunityID}`)}
                size="small"
                color={projectExists ? "primary" : "default"}
              >
                {projectExists ? (
                  <Launch sx={{ color: "white" }} />
                ) : (
                  <AddCircle sx={{ color: "white" }} />
                )}
              </IconButton>
            </span>
          </Tooltip>,
        ]
      },
      flex: 0.6,
    },
  ]

  // Combine regular leads with archived leads if showArchived is true
  const displayedLeads = showArchived ? [...leads, ...leadsArchive] : leads

  if (loading) {
    return <HaufeLoader message={haufeOperationType || "Lade Daten aus dem Haufe-System..."} />
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
        <StyledButton onClick={handleLoadLeads} sx={{ mt: 2 }}>
          Erneut versuchen
        </StyledButton>
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <StyledButton
          onClick={handleLoadLeads}
          buttonType="custom"
          customText="Verkaufschancen aktualisieren"
          sx={{
            textTransform: "none",
          }}
          customIcon={
            loading ? (
              isHaufeLoading ? (
                <HaufeLoaderInline size={20} compact />
              ) : (
                <CircularProgress size={24} />
              )
            ) : undefined
          }
          variant="contained"
        />

        <StyledButton
          onClick={handleLoadArchived}
          buttonType="custom"
          customText="Archivierte Verkaufschancen laden"
          sx={{
            textTransform: "none",
          }}
          customIcon={
            loadingArchive ? (
              isHaufeLoading ? (
                <HaufeLoaderInline size={20} compact />
              ) : (
                <CircularProgress size={24} />
              )
            ) : undefined
          }
          variant="contained"
        />
      </Box>
      {showArchived && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Zeigt {leads?.length || 0} aktive und {leadsArchive?.length || 0} archivierte
            Verkaufschancen
          </Typography>
        </Box>
      )}
      {leads && (
        <DataGrid
          rows={displayedLeads || []}
          columns={columns}
          initialState={{
            sorting: { sortModel: [{ field: "opportunityID", sort: "desc" }] },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      )}
    </Box>
  )
}

export default Leads
